import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

type InfiniteScrollOptions<T> = {
  queryKey: string[];
  queryFn: (
    page: number,
    searchTerm?: string,
  ) => Promise<
    | {
        data: T[];
        page_info: {
          has_next_page: boolean;
          has_previous_page: boolean;
        };
      }
    | undefined
  >;
  searchTerm?: string;
  threshold?: number;
};

type InfiniteScrollResult<T> = {
  data: T[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => void;
  refetch: () => void;
  bottomElementRef: (node: HTMLElement | null) => void;
};

function useInfiniteScroll<T>({
  queryKey,
  queryFn,
  searchTerm = '',
  threshold = 100,
}: InfiniteScrollOptions<T>): InfiniteScrollResult<T> {
  const observerRef = useRef<IntersectionObserver | null>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: [...queryKey, searchTerm],
    queryFn: ({ pageParam = 1 }) => queryFn(pageParam, searchTerm),
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.page_info?.has_next_page) {
        return undefined;
      }
      return pages.length + 1;
    },
    enabled: true,
  });

  // Ref callback to attach intersection observer to scroll trigger element
  const bottomElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node || !hasNextPage || isFetchingNextPage) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          if (entry.isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: `${threshold}px`,
        },
      );

      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, threshold, fetchNextPage],
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const flattenedData =
    data?.pages?.reduce((acc, page) => {
      if (page?.data) {
        return [...acc, ...page.data];
      }
      return acc;
    }, [] as T[]) || [];

  return {
    data: flattenedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    error: error as Error | null,
    fetchNextPage,
    refetch,
    bottomElementRef,
  };
}

export default useInfiniteScroll;
